import React, { useEffect, useRef, useState, useCallback } from "react";
import { Share2, X, RefreshCw } from "lucide-react";
import { FaceMesh } from "@mediapipe/face_mesh";
import { FILTERS, renderFilter } from "./arFilters";
import "../styles/arCameraPage.css";

export default function ARCameraPage() {

  /* ── refs ── */
  const videoRef          = useRef(null);
  const canvasRef         = useRef(null);   // hidden — for photo capture
  const overlayRef        = useRef(null);   // visible AR canvas
  const mediaRecorderRef  = useRef(null);
  const pressTimer        = useRef(null);
  const chunksRef         = useRef([]);
  const activeFilterRef   = useRef("chefhat"); // sync for rAF loop

  /* ── state ── */
  const [capturedImage,  setCapturedImage]  = useState(null);
  const [capturedVideo,  setCapturedVideo]  = useState(null);
  const [showPreview,    setShowPreview]    = useState(false);
  const [isRecording,    setIsRecording]    = useState(false);
  const [isFrontCamera,  setIsFrontCamera]  = useState(true);
  const [activeFilter,   setActiveFilter]   = useState("chefhat");

  /* keep ref in sync so the rAF closure always reads current filter */
  useEffect(() => { activeFilterRef.current = activeFilter; }, [activeFilter]);

  /* ── restaurant watermark ── */
  const restaurantData = JSON.parse(sessionStorage.getItem("restaurantData") || "{}");
  const restaurantName = restaurantData?.restaurantName || "Restaurant";

  /* ======================
     CAMERA
  ====================== */

  const stopCamera = useCallback(() => {
    const video = videoRef.current;
    if (video?.srcObject) {
      video.srcObject.getTracks().forEach((t) => t.stop());
      video.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    stopCamera();
    try {
      const devices      = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((d) => d.kind === "videoinput");

      let deviceId;
      if (!isFrontCamera) {
        const back = videoDevices.find(
          (d) =>
            d.label.toLowerCase().includes("back") ||
            d.label.toLowerCase().includes("rear") ||
            d.label.toLowerCase().includes("environment")
        );
        deviceId = back?.deviceId;
      } else {
        deviceId = videoDevices[0]?.deviceId;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          facingMode: isFrontCamera ? "user" : "environment",
          width:  { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: true,
      });

      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) {
      console.error("Camera error:", err);
    }
  }, [isFrontCamera, stopCamera]);

  useEffect(() => {
    startCamera();
    return stopCamera;
  }, [startCamera, stopCamera]);

  /* ======================
     FACE MESH + FILTER LOOP
  ====================== */

  useEffect(() => {
    let faceMesh;
    let rafId;
    let mounted = true;

    const setup = async () => {
      const video  = videoRef.current;
      const canvas = overlayRef.current;
      if (!video || !canvas) return;

      const ctx = canvas.getContext("2d");

      faceMesh = new FaceMesh({
        locateFile: (f) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${f}`,
      });

      faceMesh.setOptions({
        maxNumFaces:           5,
        refineLandmarks:       true,   // enables iris + detailed lips
        minDetectionConfidence: 0.65,
        minTrackingConfidence:  0.65,
      });

      faceMesh.onResults((results) => {
        if (!mounted) return;

        const W = video.offsetWidth;
        const H = video.offsetHeight;

        canvas.width  = W;
        canvas.height = H;

        ctx.clearRect(0, 0, W, H);

        if (!results.multiFaceLandmarks?.length) return;

        const currentFilter = activeFilterRef.current;
        if (currentFilter === "none") return;

        results.multiFaceLandmarks.forEach((landmarks) => {
          renderFilter(ctx, currentFilter, landmarks, W, H);
        });
      });

      const detect = async () => {
        if (video.readyState >= 2 && mounted) {
          await faceMesh.send({ image: video });
        }
        rafId = requestAnimationFrame(detect);
      };

      detect();
    };

    setup();

    return () => {
      mounted = false;
      cancelAnimationFrame(rafId);
      faceMesh?.close?.();
    };
  }, [isFrontCamera]); // re-init when camera flips

  /* ======================
     CAPTURE PHOTO
  ====================== */

  const capturePhoto = useCallback(() => {
    const video   = videoRef.current;
    const canvas  = canvasRef.current;
    const overlay = overlayRef.current;
    if (!video || !canvas) return;

    const ctx = canvas.getContext("2d");
    const W   = video.offsetWidth;
    const H   = video.offsetHeight;
    const vW  = video.videoWidth;
    const vH  = video.videoHeight;

    canvas.width  = W;
    canvas.height = H;

    /* Letterbox crop to match display */
    const vRatio = vW / vH;
    const dRatio = W  / H;
    let sx = 0, sy = 0, sW = vW, sH = vH;
    if (vRatio > dRatio) {
      sW = vH * dRatio;
      sx = (vW - sW) / 2;
    } else {
      sH = vW / dRatio;
      sy = (vH - sH) / 2;
    }

    ctx.save();
    if (isFrontCamera) {
      ctx.translate(W, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, sx, sy, sW, sH, 0, 0, W, H);
    ctx.restore();

    /* Draw AR overlay */
    if (overlay) {
      ctx.save();
      if (isFrontCamera) {
        ctx.translate(W, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(overlay, 0, 0, W, H);
      ctx.restore();
    }

    /* Watermark */
    _drawWatermark(ctx, W, restaurantName);

    const image = canvas.toDataURL("image/png", 1);
    setCapturedImage(image);
    setCapturedVideo(null);
    setShowPreview(true);
  }, [isFrontCamera, restaurantName]);

  /* ======================
     RECORDING
  ====================== */

  const startRecording = useCallback(() => {
    const stream = videoRef.current?.srcObject;
    if (!stream) return;

    chunksRef.current = [];
    const recorder = new MediaRecorder(stream);
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/mp4" });
      setCapturedVideo(URL.createObjectURL(blob));
      setCapturedImage(null);
      setShowPreview(true);
    };

    recorder.start();
    setIsRecording(true);
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  /* ======================
     BUTTON PRESS EVENTS
  ====================== */

  const handlePressStart = useCallback(() => {
    pressTimer.current = setTimeout(startRecording, 300);
  }, [startRecording]);

  const handlePressEnd = useCallback(() => {
    clearTimeout(pressTimer.current);
    if (isRecording) stopRecording();
    else capturePhoto();
  }, [isRecording, stopRecording, capturePhoto]);

  /* ======================
     SHARE
  ====================== */

  const shareMedia = useCallback(async () => {
    try {
      if (!capturedImage) return;
      const res  = await fetch(capturedImage);
      const blob = await res.blob();
      const file = new File([blob], "ar-photo.png", { type: "image/png" });

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "AR Photo" });
      } else {
        alert("Sharing not supported on this device");
      }
    } catch (err) {
      console.error(err);
    }
  }, [capturedImage]);

  /* ======================
     RENDER
  ====================== */

  return (
    <div className="camera-page">

      {/* VIDEO */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={`camera-video${isFrontCamera ? "" : " mirror-video"}`}
      />

      {/* AR OVERLAY */}
      <canvas
        ref={overlayRef}
        className={`ar-overlay-canvas${isFrontCamera ? " mirror-overlay" : ""}`}
      />

      {/* HIDDEN CAPTURE CANVAS */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* CONTROLS */}
      {!showPreview && (
        <>
          {/* FILTER BAR */}
          <div className="filters-container">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                className={`filter-item${activeFilter === f.id ? " active-filter" : ""}`}
                onClick={() => setActiveFilter(f.id)}
                title={f.label}
              >
                <span className="filter-emoji">{f.emoji}</span>
                <span className="filter-label">{f.label}</span>
              </button>
            ))}
          </div>

          {/* CAMERA CONTROLS */}
          <div className="camera-controls">

            {/* PREVIEW THUMB */}
            <div
              className="preview-thumb"
              onClick={() => {
                if (capturedImage || capturedVideo) setShowPreview(true);
              }}
            >
              {capturedImage ? (
                <img src={capturedImage} alt="" />
              ) : capturedVideo ? (
                <video src={capturedVideo} />
              ) : (
                <div className="empty-preview-thumb">
                  <div className="empty-preview-inner" />
                </div>
              )}
            </div>

            {/* CAPTURE BUTTON */}
            <button
              className={`capture-btn${isRecording ? " recording" : ""}`}
              onMouseDown={handlePressStart}
              onMouseUp={handlePressEnd}
              onTouchStart={handlePressStart}
              onTouchEnd={handlePressEnd}
            >
              <div className="capture-inner" />
            </button>

            {/* FLIP CAMERA */}
            <button
              className="switch-camera-btn"
              onClick={() => setIsFrontCamera((v) => !v)}
            >
              <RefreshCw size={26} />
            </button>
          </div>
        </>
      )}

      {/* PREVIEW OVERLAY */}
      {showPreview && (
        <div className="preview-overlay">

          <div className="preview-top-bar">
            <button className="preview-icon-btn" onClick={() => setShowPreview(false)}>
              <X size={24} />
            </button>
          </div>

          <div className="preview-media-wrapper">
            {capturedImage ? (
              <img src={capturedImage} alt="" className="preview-full-image" />
            ) : (
              <video src={capturedVideo} controls autoPlay className="preview-full-image" />
            )}
          </div>

          <div className="preview-bottom-wrapper">
            <div className="preview-bottom-bar">
              <div className="social-share-container">

                <button className="preview-action-btn" onClick={() => setShowPreview(false)}>
                  <RefreshCw size={20} />
                </button>

                <button className="social-btn whatsapp-btn" onClick={shareMedia}>
                  <img src="https://cdn-icons-png.flaticon.com/512/733/733585.png" alt="WhatsApp" />
                </button>

                <button className="social-btn instagram-btn" onClick={shareMedia}>
                  <img src="https://cdn-icons-png.flaticon.com/512/2111/2111463.png" alt="Instagram" />
                </button>

                <button className="social-btn snapchat-btn" onClick={shareMedia}>
                  <img src="/snapchat.png" style={{ width: 40, height: 40 }} alt="Snapchat" />
                </button>

                <button className="share-main-btn" onClick={shareMedia}>
                  <Share2 size={20} />
                </button>

              </div>
            </div>
          </div>

        </div>
      )}

    </div>
  );
}

/* ======================
   WATERMARK HELPER
====================== */

function _drawWatermark(ctx, W, restaurantName) {
  ctx.save();

  const boxW  = 360, boxH  = 74;
  const boxX  = 20,  boxY  = 20;

  ctx.fillStyle = "rgba(15,15,15,0.32)";
  ctx.beginPath();
  ctx.roundRect(boxX, boxY, boxW, boxH, 22);
  ctx.fill();

  ctx.strokeStyle = "rgba(255,255,255,0.18)";
  ctx.lineWidth   = 1.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.fillStyle = "#ffb703";
  ctx.arc(boxX + 28, boxY + 37, 8, 0, Math.PI * 2);
  ctx.fill();

  ctx.font         = "bold 30px sans-serif";
  ctx.fillStyle    = "#ffffff";
  ctx.textBaseline = "middle";
  ctx.fillText(restaurantName, boxX + 48, boxY + 37);

  ctx.font      = "18px sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.72)";
  ctx.fillText("Scan • Explore • Enjoy", boxX + 48, boxY + 60);

  ctx.restore();
}